import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { DarkMembersSettings } from '@/store/playlist/types';
import { ModuleCard } from './ModuleCard';
import { BonusCard } from './BonusCard';
import { TemplateDarkMembersFooter } from './TemplateDarkMembersFooter';

interface TemplateDarkMembersPreviewProps {
  settings?: DarkMembersSettings;
  onModuleClick?: (moduleId: string) => void;
  onBonusClick?: (bonusId: string) => void;
}

export function TemplateDarkMembersPreview({ 
  settings,
  onModuleClick,
  onBonusClick
}: TemplateDarkMembersPreviewProps) {
  useLanguage();
  const heroImage = settings?.heroImage;
  const modules = (settings?.modules || []).filter(m => m.isActive);
  const bonuses = (settings?.bonuses || []).filter(b => b.isActive);

  return (
    <div data-theme-isolated className="min-h-screen bg-[#0E0E0E] flex flex-col overflow-x-hidden">
      {/* Hero Banner - Responsivo com img */}
      {heroImage && (
        <section className="hero-banner-section w-full relative">
          <img 
            src={heroImage}
            alt={translateInterfaceText("Banner")}
            className="hero-banner-img w-full h-auto object-cover object-center"
            decoding="async"
            loading="eager"
            fetchPriority="high"
          />
          
          {/* Sombra Gradiente Estilo Cademi */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, transparent 0%, rgba(14, 14, 14, 0.6) 50%, #0E0E0E 100%)'
            }}
          />
          
          {/* Texto Sobreposto (condicional) */}
          {settings?.showHeroText && (settings?.heroTitle || settings?.heroSubtitle) && (
            <div className="absolute inset-0 bg-black/30 flex items-center">
              <div className="px-4 md:px-8 text-center md:text-left w-full">
                {settings?.heroTitle && (
                  <h1 className="text-white text-2xl md:text-4xl font-bold">
                    {settings.heroTitle}
                  </h1>
                )}
                {settings?.heroSubtitle && (
                  <p className="text-white/80 text-sm md:text-lg mt-2">
                    {settings.heroSubtitle}
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Módulos - Grid responsivo: 2 mobile / 3 tablet / 6 desktop */}
      {modules.length > 0 && (
        <section className="px-4 md:px-8 py-6 mt-12">
          <div className="mb-6">
            <h2 className="text-white text-xl md:text-2xl font-semibold">
              {settings?.modulesSectionTitle || translateInterfaceText("Módulos")}
            </h2>
            {settings?.modulesSectionDescription && (
              <p className="text-gray-400 text-sm mt-2">
                {settings.modulesSectionDescription}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {modules.map(module => (
              <ModuleCard 
                key={module.id}
                module={module}
                onClick={() => onModuleClick?.(module.id)}
                settings={settings}
              />
            ))}
          </div>
        </section>
      )}

      {/* Bônus - Seção separada */}
      {bonuses.length > 0 && (
        <section className="px-4 md:px-8 py-6">
          <div className="mb-6">
            <h2 className="text-white text-xl md:text-2xl font-semibold">
              {settings?.bonusesSectionTitle || translateInterfaceText("Bônus")}
            </h2>
            {settings?.bonusesSectionDescription && (
              <p className="text-gray-400 text-sm mt-2">
                {settings.bonusesSectionDescription}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {bonuses.map(bonus => (
              <BonusCard 
                key={bonus.id} 
                bonus={bonus}
                settings={settings}
                onClick={() => onBonusClick?.(bonus.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {modules.length === 0 && bonuses.length === 0 && (
        <div className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-white text-xl font-semibold mb-2">
              {translateInterfaceText("Nenhum módulo ou bônus ainda")}</h3>
            <p className="text-gray-400 text-sm">
              {translateInterfaceText("Configure os módulos e bônus no painel de edição")}</p>
          </div>
        </div>
      )}

      {/* Footer Dark */}
      <TemplateDarkMembersFooter 
        showTerms={true}
        showPrivacy={true}
      />
    </div>
  );
}
